// src/components/NavigationMenu.jsx
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  FaAnchor, FaBell, FaBars, FaBookOpen, FaBoxOpen, FaCalendarAlt, FaCompass, FaEnvelope,
  FaHandHoldingHeart, FaHistory, FaHome, FaMapMarkerAlt, FaMapPin,
  FaQuestionCircle, FaSearch, FaTimes, FaTrophy, FaTruck, FaUser, FaUsers,
} from 'react-icons/fa'
import { NavLink, useLocation } from 'react-router-dom'
import { loadPiratenpostInbox } from '../api/piratenpostApi'
import { useStepsContext } from '../hooks/StepsContext'

const FOCUSABLE = 'a[href],button:not([disabled]),[tabindex]:not([tabindex="-1"])'

export function NavigationMenu({ unreadCount = 0 }) {
  const location = useLocation()
  const { clearTemporaryState } = useStepsContext()
  const [open, setOpen] = useState(false)
  const [mailUnreadCount, setMailUnreadCount] = useState(0)
  const menuButtonRef = useRef(null)
  const menuPanelRef = useRef(null)
  const restoreFocusRef = useRef(false)
  const previousPathRef = useRef(location.pathname)

  const isLoggedIn = !!window.pfandpiratData?.user_id
  const userName = window.pfandpiratData?.display_name || 'Nutzer'
  const totalUnreadCount = Number(unreadCount || 0) + Number(mailUnreadCount || 0)
  const menuRouteActive = !['/overview', '/map', '/crews', '/inventar'].includes(location.pathname)
  const base = window.location.origin
  const loginBaseUrl = window.pfandpiratData?.loginUrl || '/wp-login.php'
  const appBaseUrl = window.pfandpiratData?.appUrl || '/app/'
  const bridgeUrl = new URL(appBaseUrl, base)
  bridgeUrl.searchParams.set('pfp_after_login', '1')
  bridgeUrl.searchParams.set('pfp_next', '#/overview')
  const loginUrlObject = new URL(loginBaseUrl, base)
  loginUrlObject.searchParams.set('redirect_to', bridgeUrl.toString())
  const loginUrl = loginUrlObject.toString()
  const logoutUrl = `/wp-login.php?action=logout&redirect_to=${encodeURIComponent(base + '/app/')}`

  const closeMenu = useCallback((restoreFocus = true) => {
    restoreFocusRef.current = restoreFocus
    setOpen(false)
  }, [])

  useEffect(() => {
    if (open || !restoreFocusRef.current) return undefined
    restoreFocusRef.current = false
    const frame = window.requestAnimationFrame(() => menuButtonRef.current?.focus())
    return () => window.cancelAnimationFrame(frame)
  }, [open])

  useEffect(() => {
    if (previousPathRef.current !== location.pathname) {
      previousPathRef.current = location.pathname
      setOpen(false)
    }
  }, [location.pathname])

  useEffect(() => {
    if (!open) return undefined
    const appContent = document.getElementById('pfp-app-content')
    const previousAriaHidden = appContent?.getAttribute('aria-hidden')
    if (appContent) {
      appContent.inert = true
      appContent.setAttribute('aria-hidden', 'true')
    }
    window.requestAnimationFrame(() => menuPanelRef.current?.querySelector('.pfandpirat-dropdown__links a')?.focus())
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        closeMenu()
        return
      }
      if (event.key !== 'Tab') return
      const focusable = [...(menuPanelRef.current?.querySelectorAll(FOCUSABLE) || [])]
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      if (appContent) {
        appContent.inert = false
        if (previousAriaHidden === null) appContent.removeAttribute('aria-hidden')
        else appContent.setAttribute('aria-hidden', previousAriaHidden)
      }
    }
  }, [closeMenu, open])

  useEffect(() => {
    if (!isLoggedIn) { setMailUnreadCount(0); return undefined }
    let cancelled = false
    const refreshMailCount = () => {
      loadPiratenpostInbox()
        .then((data) => { if (!cancelled) setMailUnreadCount(Number(data?.unread_count || 0)) })
        .catch(() => { if (!cancelled) setMailUnreadCount(0) })
    }
    refreshMailCount()
    const interval = window.setInterval(refreshMailCount, 60000)
    window.addEventListener('focus', refreshMailCount)
    return () => { cancelled = true; window.clearInterval(interval); window.removeEventListener('focus', refreshMailCount) }
  }, [isLoggedIn])

  const menuLink = (to, icon, label, suffix = '') => (
    <NavLink to={to} onClick={() => closeMenu(false)}>{icon}{label}{suffix}</NavLink>
  )

  const navigation = (
    <>
      {open && (
        <div className="pfandpirat-menu-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget) closeMenu() }}>
          <section ref={menuPanelRef} id="pfandpirat-main-menu" className="pfandpirat-dropdown" role="dialog" aria-modal="true" aria-labelledby="pfandpirat-main-menu-title">
            <header className="pfandpirat-dropdown__header">
              <div><strong id="pfandpirat-main-menu-title">Hauptmenü</strong><span>Alle Bereiche der Pfandpirat-App</span></div>
              <button type="button" className="pfandpirat-dropdown__close" aria-label="Hauptmenü schließen" onClick={() => closeMenu()}><FaTimes aria-hidden="true" /></button>
            </header>
            <nav className="pfandpirat-dropdown__links" aria-label="Vollständiges Hauptmenü">
              {menuLink('/overview', <FaHome aria-hidden="true" />, 'Übersicht')}
              {menuLink('/map', <FaMapMarkerAlt aria-hidden="true" />, 'Karte & Pfand-Orte')}
              {menuLink('/search', <FaSearch aria-hidden="true" />, 'Getränkesuche & EAN-Scanner')}
              {menuLink('/collection', <FaBookOpen aria-hidden="true" />, 'Getränke-Logbuch')}
              {menuLink('/history', <FaHistory aria-hidden="true" />, 'Pfandverlauf')}
              {menuLink('/profile', <FaUser aria-hidden="true" />, 'Profil')}
              {menuLink('/inventar', <FaBoxOpen aria-hidden="true" />, 'Inventar')}
              {menuLink('/crews', <FaUsers aria-hidden="true" />, 'Crews')}
              {menuLink('/freibeuterhafen', <FaAnchor aria-hidden="true" />, 'Freibeuterhafen')}
              {menuLink('/quick-mark', <FaMapPin aria-hidden="true" />, 'Pfand für Sammler markieren')}
              {menuLink('/donate', <FaHandHoldingHeart aria-hidden="true" />, 'Pfand spenden')}
              {menuLink('/expeditions', <FaCompass aria-hidden="true" />, 'Expeditionen')}
              {menuLink('/events', <FaCalendarAlt aria-hidden="true" />, 'Events')}
              {menuLink('/piratenpost', <FaEnvelope aria-hidden="true" />, 'Piratenpost', mailUnreadCount > 0 ? ` (${mailUnreadCount})` : '')}
              {menuLink('/getraenke-liefern', <FaTruck aria-hidden="true" />, 'Getränke liefern lassen')}
              {menuLink('/leaderboard', <FaTrophy aria-hidden="true" />, 'Leaderboard')}
              {menuLink('/news', <FaBell aria-hidden="true" />, 'Neuigkeiten', unreadCount > 0 ? ` (${unreadCount})` : '')}
              {menuLink('/hilfe', <FaQuestionCircle aria-hidden="true" />, 'Hilfe & Spielregeln')}
            </nav>
            <div className="pfandpirat-dropdown__auth">
              {!isLoggedIn ? <a href={loginUrl} className="pfandpirat-dropdown-auth">Anmelden</a> : <a href={logoutUrl} className="pfandpirat-dropdown-auth is-logout" onClick={clearTemporaryState}>Abmelden ({userName})</a>}
            </div>
          </section>
        </div>
      )}

      <nav className="pfandpirat-nav" aria-label="Hauptnavigation">
        <NavLink to="/overview" aria-label="Übersicht" className={({ isActive }) => `pfandpirat-nav__item${isActive ? ' is-active' : ''}`}><FaHome size={22} aria-hidden="true" /><span>Übersicht</span></NavLink>
        <NavLink to="/map" aria-label="Karte" className={({ isActive }) => `pfandpirat-nav__item${isActive ? ' is-active' : ''}`}><FaMapMarkerAlt size={22} aria-hidden="true" /><span>Karte</span></NavLink>
        <button ref={menuButtonRef} type="button" className={`pfandpirat-nav__item pfandpirat-hamburger${open || menuRouteActive ? ' is-active' : ''}`} aria-label={open ? 'Hauptmenü schließen' : 'Hauptmenü öffnen'} aria-expanded={open} aria-current={menuRouteActive ? 'page' : undefined} aria-controls="pfandpirat-main-menu" onClick={() => open ? closeMenu() : setOpen(true)}>
          <span className="pfandpirat-hamburger__icon"><FaBars size={24} aria-hidden="true" />{totalUnreadCount > 0 && <span className="pfp-nav-badge">{totalUnreadCount > 9 ? '9+' : totalUnreadCount}</span>}</span><span>Menü</span>
        </button>
        <NavLink to="/crews" aria-label="Crew" className={({ isActive }) => `pfandpirat-nav__item${isActive ? ' is-active' : ''}`}><FaUsers size={22} aria-hidden="true" /><span>Crew</span></NavLink>
        <NavLink to="/inventar" aria-label="Inventar" className={({ isActive }) => `pfandpirat-nav__item${isActive ? ' is-active' : ''}`}><FaBoxOpen size={22} aria-hidden="true" /><span>Inventar</span></NavLink>
      </nav>
    </>
  )

  return typeof document === 'undefined' ? navigation : createPortal(navigation, document.body)
}
